import { Component, Input, OnInit, inject, ChangeDetectorRef } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { SupplierModel } from 'src/app/models/supplier.model';
import { WorkingAreaResponseModel } from 'src/app/models/workingArea-response.model';
import { SupplierService } from 'src/app/services/supplier.services';
import { ToastService } from 'src/app/services/toast.service';
import { AreaTypeEnum } from 'src/app/shared/enumerators/area-type.enum';
import { UserService } from 'src/app/shared/services/user.service';
import { InduMapComponent } from '../../../shared/components/indu-map/indu-map.component';
import { WorkingAreaRequestModel } from 'src/app/models/workingArea-request.model';
import { forkJoin } from 'rxjs';

@Component({
  selector: 'app-supplier-working-areas',
  templateUrl: './supplier-working-areas.component.html',
  styleUrls: ['./supplier-working-areas.component.scss'],
  imports: [InduMapComponent]
})
export class SupplierWorkingAreasComponent implements OnInit {
  private readonly supplierService = inject(SupplierService);
  private readonly cdr = inject(ChangeDetectorRef);

  activatedRoute = inject(ActivatedRoute);
  toastr = inject(ToastService);
  usrSvc = inject(UserService);

  supplierId!: string;
  center!: google.maps.LatLngLiteral;
  @Input() supplier!: SupplierModel;

  circleCenter: any[] = [];
  private deletedAreaIds: string[] = [];
  enableCircleClick: boolean = false;

  ngOnInit(): void {
    this.supplierId = this.supplier.id.toString();
    this.loadWorkingAreas(); // Usamos un método dedicado
  }

  loadWorkingAreas() {
    if (!this.supplierId) return;

    this.supplierService.getWorkingAreas(this.supplierId).subscribe((data: WorkingAreaResponseModel[]) => {
      // Mapeamos los datos para el mapa
      this.circleCenter = data.map(area => ({
        id: area.id,
        lat: area.latitude,
        lng: area.longitude,
        radiusKm: area.radiusKm,
        options: {
          fillColor: area.areaType == AreaTypeEnum.Inclusion ? 'green' : 'red',
          strokeColor: area.areaType == AreaTypeEnum.Inclusion ? 'green' : 'red',
          areaType: area.areaType,
          clickable: this.enableCircleClick, // Se vuelve clickeable solo en edición
          strokeOpacity: 0.8,
          strokeWeight: 1,
          fillOpacity: 0.35,
          editable: false,
          draggable: false
        },
      }));
      this.setCenter();
    });
  }

  toggleEditMode() {
    this.enableCircleClick = !this.enableCircleClick;
    // Refrescamos las opciones de los círculos para que sean clickeables o no
    this.loadWorkingAreas();
  }

  // Asegúrate de que esta función ponga editable/draggable en true si enableCircleClick es true
  private mapToCircle(area: any) {
    return {
      id: area.id || '',
      lat: area.latitude,
      lng: area.longitude,
      radiusKm: area.radiusKm, // Siempre en KM
      options: {
        fillColor: area.areaType == 1 ? 'green' : 'red',
        strokeColor: area.areaType == 1 ? 'green' : 'red',
        areaType: area.areaType,
        clickable: true,
        editable: this.enableCircleClick,
        draggable: this.enableCircleClick,
        fillOpacity: 0.35,
        strokeWeight: 1
      }
    };
  }
  // En supplier-working-areas.component.ts

  // 1. Corregimos el borrado local para evitar el error 500
  onLocalDelete(circle: any) {
    // Solo agregamos a la lista de borrado de DB si es un ID real (no temporal)
    if (circle.id && !String(circle.id).startsWith('temp_')) {
      this.deletedAreaIds.push(circle.id);
    }
    // Lo quitamos de la vista inmediatamente
    this.circleCenter = this.circleCenter.filter(c => c !== circle);
    this.cdr.detectChanges();
  }

  // 2. Actualización local sin "spam"
  onLocalUpdate(zoneData: any) {
    const index = this.circleCenter.findIndex(c => String(c.id) === String(zoneData.id));

    if (index !== -1) {
      // Actualizamos los valores directamente en el objeto
      this.circleCenter[index].lat = zoneData.latitude;
      this.circleCenter[index].lng = zoneData.longitude;
      this.circleCenter[index].radiusKm = zoneData.radiusKm;

      // IMPORTANTE: NO hacemos this.circleCenter = [...this.circleCenter] aquí
      // para que el mapa no destruya el círculo mientras el usuario lo mueve.
    } else {
      // Es nuevo, lo agregamos
      const newCircle = this.mapToCircle(zoneData);
      this.circleCenter = [...this.circleCenter, newCircle];
      this.cdr.detectChanges();
    }
  }

  // 3. Guardado final con logs para que veas qué se envía
  saveAllChanges() {
    const saveRequests: any = [];

    // Borrados reales
    this.deletedAreaIds.forEach(id => {
      saveRequests.push(this.supplierService.deleteSupplierWorkingArea(id));
    });

    // Creaciones y Ediciones
    this.circleCenter.forEach(circle => {
      const payload: WorkingAreaRequestModel = {
        latitude: circle.lat,
        longitude: circle.lng,
        radiusKm: Number(circle.radiusKm),
        areaType: circle.options.areaType
      };

      const isTemp = String(circle.id).startsWith('temp_');
      if (circle.id && !isTemp) {
        saveRequests.push(this.supplierService.updateSupplierWorkingArea(circle.id, payload));
      } else {
        saveRequests.push(this.supplierService.addSupplierWorkingArea(this.supplierId, payload));
      }
    });

    if (saveRequests.length === 0) {
      this.enableCircleClick = false;
      return;
    }

    forkJoin(saveRequests).subscribe({
      next: () => {
        this.toastr.success('Zonas', 'Cambios guardados con éxito');
        this.enableCircleClick = false;
        this.deletedAreaIds = [];
        this.loadWorkingAreas();
      },
      error: (err) => {
        this.toastr.error('Error', 'No se pudieron sincronizar todos los cambios');
      }
    });
  }

  cancelEdits() {
    this.enableCircleClick = false;
    this.deletedAreaIds = [];
    this.loadWorkingAreas(); // Revertimos cambios locales recargando de la DB
  }

  setCenter() {
    if (this.circleCenter.length == 1) {
      this.center = {
        lat: this.circleCenter[0].lat,
        lng: this.circleCenter[0].lng,
      };
    } else if (this.circleCenter.length > 1) {
      let InclusionArea = this.circleCenter.find(
        (area) => area.options.areaType == AreaTypeEnum.Inclusion
      );
      if (InclusionArea) {
        this.center = {
          lat: InclusionArea.lat,
          lng: InclusionArea.lng,
        };
      } else {
        this.center = {
          lat: this.circleCenter[0].lat,
          lng: this.circleCenter[0].lng,
        };
      }
    }
  }
}
